import { useMemo } from 'react'
import { formatMoney } from '../lib/format.js'

// Tap-the-tile breakdown.
// Bottom sheet that shows where the headline savings number came from:
//   1. Lifetime headline (matches what's on the tile)
//   2. By-source breakdown — small horizontal bars showing each
//      source's share of the lifetime total
//   3. Full event list — date, source, methodology, amount, plus
//      a verified/preliminary indicator
//
// Read-only. The attestation modal (separate component) is where the
// owner actually confirms events. From here, "Review preliminary" is
// a shortcut button that defers to that modal.
export default function SavingsBreakdownSheet({
  open,
  onClose,
  events,
  summary,
  onReviewClick,
}) {
  if (!open) return null

  const lifetime    = Number(summary?.lifetime_total ?? 0)
  const confirmed   = Number(summary?.confirmed_total ?? 0)
  const preliminary = Number(summary?.preliminary_total ?? 0)
  const eventCount  = Number(summary?.event_count ?? 0)
  const confirmedCount = Number(summary?.confirmed_count ?? 0)
  const preliminaryCount = eventCount - confirmedCount

  // Friendly source label — same mapping as the attestation modal.
  // Keep these in sync; future refactor: extract to a shared helper.
  const sourceLabel = (s) => {
    switch (s) {
      case 'daily_under_target':  return 'Daily under target'
      case 'waste_avoided':       return 'Waste avoided'
      case 'reorder_reduction':   return 'Reorder reduction'
      case 'par_optimization':    return 'Par optimization'
      case 'supplier_swap':       return 'Supplier swap'
      case 'recipe_costing':      return 'Recipe costing'
      case 'menu_engineering':    return 'Menu engineering'
      case 'labor_optimization':  return 'Labor optimization'
      case 'theft_detection':     return 'Theft detection'
      case 'marketing_lift':      return 'Marketing lift'
      case 'manual_attestation':  return 'Manual attestation'
      default:                    return s
    }
  }

  // Aggregate by source for the bar breakdown.
  const bySource = useMemo(() => {
    const map = new Map()
    for (const e of events ?? []) {
      const prev = map.get(e.source) || { count: 0, total: 0 }
      map.set(e.source, {
        count: prev.count + 1,
        total: prev.total + Number(e.dollar_amount || 0),
      })
    }
    return [...map.entries()]
      .map(([source, v]) => ({ source, ...v }))
      .sort((a, b) => b.total - a.total)
  }, [events])

  const fmtDate = (iso) => {
    if (!iso) return ''
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Savings breakdown"
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">Savings breakdown</h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Close"
            >
              Close
            </button>
          </div>
        </div>

        <div className="space-y-6 px-4 pb-4">
          {/* Headline mirrors the LifetimeSavings tile so users know
              they're looking at the same number, just expanded. */}
          <section>
            <div className="text-xs font-medium text-emerald-700">
              AI Restaurant Manager has saved you
            </div>
            <div className="mt-1 flex items-baseline gap-3">
              <span className="text-4xl font-medium tabular-nums text-emerald-700">
                {formatMoney(lifetime)}
              </span>
              <span className="text-sm text-black/60">Lifetime</span>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg bg-emerald-50 px-3 py-2">
                <div className="text-xs text-emerald-900/70">✓ Verified</div>
                <div className="font-medium tabular-nums text-emerald-700">
                  {formatMoney(confirmed)}
                </div>
                <div className="text-xs text-black/55">
                  {confirmedCount} {confirmedCount === 1 ? 'event' : 'events'}
                </div>
              </div>
              <div className="rounded-lg bg-amber-50 px-3 py-2">
                <div className="text-xs text-amber-900/70">Preliminary</div>
                <div className="font-medium tabular-nums text-amber-700">
                  {formatMoney(preliminary)}
                </div>
                <div className="text-xs text-black/55">
                  {preliminaryCount} {preliminaryCount === 1 ? 'event' : 'events'} pending
                </div>
              </div>
            </div>
            {preliminaryCount > 0 && onReviewClick && (
              <button
                type="button"
                onClick={() => {
                  // Defer to the attestation modal. Close this sheet so
                  // the user lands cleanly in the review flow.
                  onClose?.()
                  setTimeout(() => onReviewClick(), 50)
                }}
                className="mt-2 min-h-btn w-full rounded-lg border border-emerald-600 bg-white px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
              >
                Review preliminary events →
              </button>
            )}
          </section>

          {/* By-source breakdown — horizontal bars sized by share of
              lifetime total. Hides when there are no events. */}
          {bySource.length > 0 && lifetime > 0 && (
            <section>
              <div className="mb-2 text-base font-medium">By source</div>
              <ul className="space-y-2">
                {bySource.map(({ source, total, count }) => {
                  const pct = (total / lifetime) * 100
                  return (
                    <li key={source}>
                      <div className="flex items-baseline justify-between text-sm">
                        <span className="font-medium">
                          {sourceLabel(source)}
                          <span className="ml-2 text-xs text-black/55">
                            {count} {count === 1 ? 'event' : 'events'}
                          </span>
                        </span>
                        <span className="tabular-nums font-medium">
                          {formatMoney(total)}
                          <span className="ml-2 text-xs text-black/55">
                            {pct.toFixed(0)}%
                          </span>
                        </span>
                      </div>
                      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-black/5">
                        <div
                          className="h-full bg-emerald-600"
                          style={{ width: `${Math.max(2, pct)}%` }}
                          aria-hidden="true"
                        />
                      </div>
                    </li>
                  )
                })}
              </ul>
            </section>
          )}

          {/* Full event list, newest first. */}
          <section>
            <div className="mb-2 flex items-baseline justify-between">
              <div className="text-base font-medium">All events</div>
              <div className="text-xs text-black/55">
                {eventCount} total
              </div>
            </div>
            {eventCount === 0 ? (
              <div className="rounded-xl border border-dashed border-black/15 px-4 py-8 text-center text-sm text-black/60">
                No savings events recorded yet. The first one lands at
                end-of-day if today's waste stays under target.
              </div>
            ) : (
              <ul className="overflow-hidden rounded-xl border border-black/10">
                {events.map((e) => (
                  <li
                    key={e.id}
                    className="flex items-start justify-between gap-3 border-b border-black/5 px-4 py-3 last:border-b-0"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                        <span className="text-black/60">
                          {fmtDate(e.occurred_at)}
                        </span>
                        <span className="text-black/40">·</span>
                        <span className="font-medium text-black/80">
                          {sourceLabel(e.source)}
                        </span>
                        {e.owner_confirmed ? (
                          <span className="ml-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                            ✓ Verified
                          </span>
                        ) : (
                          <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                            Preliminary
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-black/55 leading-snug">
                        {e.calculation_method}
                      </div>
                    </div>
                    <div className="text-right text-base font-medium tabular-nums text-emerald-700">
                      {formatMoney(e.dollar_amount)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}
